import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createApiClient, getSessionToken } from '@room/frontend-sdk';
import { registerDefaultTiles, setAuthAPI, applyThemeFromQuery } from '@room/shared-ui';
import { App } from './App';
import './index.css';
import '@room/shared-ui/styles/themes/index.css';

applyThemeFromQuery();

// Unregister any old service workers to prevent caching issues
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    registrations.forEach((registration) => {
      registration.unregister().then((success) => {
        if (success) {
          console.log('[Home] Unregistered old service worker');
        }
      });
    });
  });
}

// Register default home tiles (Weather, Chat, Store, etc.)
registerDefaultTiles();

// API client for general use by home app components.
// Home page suppresses login redirect - useBackgroundAuth handles auth silently.
createApiClient({
  baseURL: '/api',
  onUnauthorized: () => {
    // No-op on home: background auth handles re-authentication
  },
});

// Auth API for bundle apps (loaded inside home's overlay).
// Uses raw fetch to avoid any api client side-effects (removeSessionToken, redirects).
// useBackgroundAuth handles the home page's own auth lifecycle independently.
setAuthAPI({
  getSession: async () => {
    const token = getSessionToken();
    const response = await fetch('/api/auth/session', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });

    if (!response.ok) {
      return { success: false, error: 'Failed to fetch session' };
    }

    return response.json();
  },
});

const queryClient = new QueryClient();

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </StrictMode>
);
